import styled, { css } from 'styled-components';
import { inlineFlexCenterAll } from '../../styles/mixins';
import spacing from '../../styles/spacing';
import { TargetContainer } from '../HBPopover/HBPopover.styles';
import { vars } from '../../theme';

const roundCorners = '3px';

/* Selected */
const selected = {
    on: {
        background: vars.colors.surface.accent,
        borderColor: vars.colors.stroke.accent,
        color: vars.colors.text.accent,
        borderRightWidth: '1px'
    },
    off: {
        background: vars.colors.surface.item,
        borderColor: vars.colors.stroke.primary,
        color: vars.colors.text.secondary
    }
};

export const IconContainer = styled.div<{
    active: boolean;
    ignoreActiveStyles: boolean;
}>`
    ${({ ignoreActiveStyles, active }) =>
        !ignoreActiveStyles &&
        css`
            i {
                svg {
                    path {
                        fill: ${active
                            ? vars.colors.text.accent
                            : vars.colors.text.tertiary};
                    }
                }
            }
        `}
`;

export const Button = styled.button<{
    active: boolean;
    disabled: boolean;
    hasIcon: boolean;
    setIconOnTopOfTheText: boolean;
}>`
    flex: 1;
    font-size: 14px;
    font-weight: normal;
    outline: none;
    cursor: pointer;
    font-family: ${vars.fonts.body};
    ${inlineFlexCenterAll}
    border-width: 1px;
    border-style: solid;
    border-right-width: 0;
    box-sizing: border-box;
    padding: 0;

    ${({ hasIcon, setIconOnTopOfTheText }) =>
        hasIcon &&
        setIconOnTopOfTheText &&
        css`
            display: flex;
            align-items: center;
            flex-direction: column;
            justify-content: space-between;
            padding: ${spacing[8]} 0;
        `}

    /* Override angular css */
    &,
    &:focus {
        ${({ active }) => (active ? selected.on : selected.off)};
    }
    transition: color 200ms ease;

    ${({ active }) =>
        !active &&
        css`
            &:hover {
                color: ${vars.colors.text.primary};
                transition: color 200ms ease;
                background: ${vars.colors.surface.itemHover};
            }
        `}

    ${({ active }) =>
        active &&
        css`
            &:hover {
                background: ${selected.on.background};
                color: ${selected.on.color};
            }
        `}
    
    ${({ disabled }) =>
        disabled &&
        css`
            color: ${vars.colors.text.disabled} !important;
            background-color: ${vars.colors.surface.item} !important;
            border-color: ${vars.colors.stroke.disabled} !important;

            + button[disabled] {
                border-left-width: 0;
            }
        `}

    ${({ active }) => active && '+ button { border-left-width: 0 }'}
`;

export const ButtonLabel = styled.span<{ isSingleLineText: boolean }>`
    padding: 0 ${spacing[4]};
    ${({ isSingleLineText }) =>
        isSingleLineText &&
        css`
            white-space: nowrap;
        `}
`;

export const ButtonGroup = styled.div<{
    width?: string;
    height?: string;
    disabled?: boolean;
}>`
    display: flex;

    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
            pointer-events: none;
        `}

    ${({ width }) =>
        width &&
        css`
            width: ${width};
        `}
    ${({ height }) =>
        height &&
        css`
            height: ${height};
        `}
    ${({ height }) =>
        !height &&
        css`
            min-height: 34px;
        `}
    
      > {
        ${TargetContainer}:first-child ${Button}, ${Button}:first-child {
            border-top-left-radius: ${roundCorners};
            border-bottom-left-radius: ${roundCorners};
        }

        ${TargetContainer}:last-child ${Button}, ${Button}:last-child {
            border-right-width: 1px;
            border-top-right-radius: ${roundCorners};
            border-bottom-right-radius: ${roundCorners};
        }

        ${TargetContainer} {
            &.active {
                + * {
                    border-left-width: 0;
                    button {
                        border-left-width: 0;
                    }
                }
            }
        }
    }
`;
